import { forwardRef } from 'react';
import { ListBox, SkeletonLine, Panel } from '@crazyegginc/hatch';

import { PanelHeader } from './PanelHeader';

import { abbreviateNumber } from '/src/utils';
import { getEmptyValues } from '../constants';

const MAX_BAR_WIDTH_PERCENTAGE = 85;

const EMPTY_VALUES = getEmptyValues();

export const RelativeBarChartPanel = forwardRef(
  (
    {
      title,
      slug,
      valueLabel = 'Visitors',
      valueKey = 'visitors',
      data = {},
      isLoading = false,
      renderIcon = null,
      rowFormatter = null,
      takeCount = 6,
      instantHeatmaps = null,
    },
    ref,
  ) => {
    const dataView = (data?.data ?? []).slice(0, takeCount);
    const hasData = dataView.length > 0;
    const fallbackData = EMPTY_VALUES?.[slug] ?? [];

    const base = hasData ? dataView : fallbackData;
    const maxValue = base.length ? Math.max(...base.map((d) => d[valueKey])) : 1;

    return (
      <li className="list-none" data-testid="bar-panel" ref={ref}>
        <Panel variant="secondary" secondaryHover={true} className="h-full">
          <PanelHeader slug={slug} title={title} subTitle={valueLabel} disabled={!hasData} />
          <ul className="w-full space-y-1.25">
            {isLoading ? (
              <SkeletonPanelContent />
            ) : (
              base.map((d, index) => {
                return (
                  <li className="flex w-full items-center text-sm" key={`${slug}+${index}`} data-testid="panel-row">
                    <ListBox
                      withBackground={hasData}
                      use={Math.floor((d[valueKey] / maxValue) * MAX_BAR_WIDTH_PERCENTAGE)}
                    >
                      <div className="flex items-center truncate w-full">
                        <div className="flex items-center flex-1 truncate">
                          {renderIcon ? renderIcon(d) : null}
                          <span className="truncate ml-1.5 text-black-pearl-500">
                            {rowFormatter ? rowFormatter(d) : d.name === '' ? '\u00A0' : d.name}
                          </span>
                        </div>
                        <div className="flex items-center text-right text-charcoal-gray-500 lowercase">
                          {instantHeatmaps ? instantHeatmaps(d) : null}
                          <div className="w-[36px] ml-2.5">{abbreviateNumber(d[valueKey])}</div>
                        </div>
                      </div>
                    </ListBox>
                  </li>
                );
              })
            )}
          </ul>
        </Panel>
      </li>
    );
  },
);

function SkeletonPanelContent() {
  const items = new Array(6).fill(true);
  return (
    <>
      {items.map((_, index) => (
        <li className="flex w-full" key={`skeleton-${index}`}>
          <SkeletonLine className="w-full !h-[36px] !rounded" />
        </li>
      ))}
    </>
  );
}
